import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import CustomButton from "./CustomButton";
import CustomInput from "./CustomInput";
import CustomTextarea from "./CustomTextarea";
import CustomCheckbox from "./CustomCheckbox";
import CustomRadioButton from "./CustomRadioButton";
import CustomSelect from "./CustomSelect";
import CustomDateTimePicker from "./CustomDateTimePicker";
import CustomText from "./CustomText";
import CustomDivider from "./CustomDivider";
import CustomRichTextbox from "./CustomRichTextbox";
import CustomTreeView from "./CustomTreeView";
import CustomAutocompleteInput from "./CustomAutocompleteInput";
import { TreeNode } from "./CustomTreeView";
import CustomFileUploader, { AllowedTypes } from "./CustomFileUploader";

enum ElementType {
  INPUT = "input",
  TEXTAREA = "textarea",
  CHECKBOX = "checkbox",
  RADIO = "radio",
  SELECT = "select",
  DATETIMEPICKER = "datetimepicker",
  TEXT = "text",
  DIVIDER = "divider",
  RICHTEXTBOX = "richtextbox",
  TREEVIEW = "treeview",
  BUTTON = "button",
  AUTOCOMPLETEINPUT = "autocompleteinput",
  FILEUPLOADER = "fileuploader",
}

// Form Elemanlarının Türleri
interface InputElement {
  id?: string;
  type: ElementType;
  label: string;
  value: string;
  rowId?: number; // Hangi satır
  colId?: number; // Hangi sütun
  innerRowId?: number; // Hangi iç satır
  colClass?: string; // Bootstrap sütun genişliği
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface TextareaElement {
  id?: string;
  type: ElementType;
  label: string;
  value: string;
  rowId?: number; // Hangi satır
  colId?: number; // Hangi sütun
  innerRowId?: number; // Hangi iç satır
  colClass?: string; // Bootstrap sütun genişliği
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

interface CheckboxElement {
  id?: string;
  type: ElementType;
  label: string;
  value: boolean;
  rowId?: number; // Hangi satır
  colId?: number; // Hangi sütun
  innerRowId?: number; // Hangi iç satır
  colClass?: string; // Bootstrap sütun genişliği
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface RadioButtonElement {
  id?: string;
  type: ElementType;
  label: string;
  value: string;
  options: {
    id: string;
    label: string;
    value: string;
  }[];
  rowId?: number; // Hangi satır
  colId?: number; // Hangi sütun
  innerRowId?: number; // Hangi iç satır
  colClass?: string; // Bootstrap sütun genişliği
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface SelectElement {
  id?: string;
  type: ElementType;
  label: string;
  value: string[];
  options: {
    id: string;
    label: string;
    value: string;
  }[];
  rowId?: number; // Hangi satır
  colId?: number; // Hangi sütun
  innerRowId?: number; // Hangi iç satır
  colClass?: string; // Bootstrap sütun genişliği
  onChange: (value: string | string[]) => void;
}

interface DateTimePickerElement {
  id?: string;
  type: ElementType;
  label: string;
  value: Date | null;
  rowId?: number; // Hangi satır
  colId?: number; // Hangi sütun
  innerRowId?: number; // Hangi iç satır
  colClass?: string; // Bootstrap sütun genişliği
  onChange: (e: Date | null) => void;
}

interface TextElement {
  id: string;
  type: ElementType;
  value: string;
  rowId?: number; // Hangi satır
  colId?: number; // Hangi sütun
  innerRowId?: number; // Hangi iç satır
  colClass?: string; // Bootstrap sütun genişliği
  style?: React.CSSProperties;
  className?: string;
}

interface DividerElement {
  id?: string;
  type: ElementType;
  rowId?: number; // Hangi satır
  colId?: number; // Hangi sütun
  innerRowId?: number; // Hangi iç satır
  colClass?: string; // Bootstrap sütun genişliği
  className?: string;
  style?: React.CSSProperties;
}

interface RichTextboxElement {
  id?: string;
  type: ElementType;
  label: string;
  value: string;
  rowId?: number;
  colId?: number;
  innerRowId?: number;
  colClass?: string;
  onChange: (value: string) => void;
}

interface TreeViewElement {
  id?: string;
  type: ElementType;
  label?: string;
  value: string | null;
  treeData: TreeNode[];
  rowId?: number;
  colId?: number;
  innerRowId?: number;
  colClass?: string;
  onChange: (selected: string | null) => void;
}

interface ButtonElement {
  id?: string;
  type: ElementType;
  label: string;
  className?: string;
  onClick: (event: React.FormEvent) => void;
  rowId?: number;
  colId?: number;
  innerRowId?: number;
  colClass?: string;
}

interface AutoCompleteInputElement {
  id?: string;
  type: ElementType;
  label: string;
  value: string;
  rowId?: number;
  colId?: number;
  innerRowId?: number;
  colClass?: string;
  fetchOptions: (query: string) => Promise<string[]>;
  onSelect: (value: string) => void;
}

interface FileUploaderElement {
  id: string;
  type: ElementType.FILEUPLOADER;
  label?: string;
  url: string;
  multi: boolean;
  allowedTypes: AllowedTypes;
  maxFile: number;
  maxSize: number;
  onUploadComplete: (uploadedFileNames: string[]) => void;
  onRemoveUploaded: (fileName: string) => void;
  onPendingChange: (pendingFileCount: number) => void;
  rowId?: number;
  colId?: number;
  innerRowId?: number;
  colClass?: string;
}

// Mevcut Türlerle Birleştirme
type FormElement =
  | InputElement
  | TextareaElement
  | CheckboxElement
  | RadioButtonElement
  | SelectElement
  | DateTimePickerElement
  | TextElement
  | DividerElement
  | RichTextboxElement
  | TreeViewElement
  | ButtonElement
  | AutoCompleteInputElement
  | FileUploaderElement;

interface CustomFormProps {
  elements: FormElement[];
  innerRowCustomClass?: string;
  onSubmit: (formValues: Record<string, any>) => void;
  confirmLabel?: string; // Confirm butonu etiketi
  cancelLabel?: string; // Cancel butonu etiketi
  handleCancel?: () => void; // Cancel butonu işlevi
  showConfirmButton?: boolean; // Confirm butonunun görünürlüğü
  showCancelButton?: boolean; // Cancel butonunun görünürlüğü
}

const CustomForm: React.FC<CustomFormProps> = ({
  elements,
  innerRowCustomClass,
  onSubmit,
  confirmLabel = "Confirm", // Varsayılan değer
  cancelLabel = "Cancel", // Varsayılan değer
  handleCancel,
  showConfirmButton = true, // Varsayılan olarak görünür
  showCancelButton = false, // Varsayılan olarak görünür
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = formRef.current;
    if (!form) return;

    const isValid = form.checkValidity();
    if (isValid) {
      setIsLoading(true);
      try {
        const formValues: Record<string, any> = {};

        Array.from(form.elements).forEach((element) => {
          if (
            element instanceof HTMLInputElement ||
            element instanceof HTMLTextAreaElement ||
            element instanceof HTMLSelectElement
          ) {
            if (element.id) {
              //RichTextbox boş göndermemesi için
              const name = element.name || element.id;

              if (
                element instanceof HTMLInputElement &&
                element.type === ElementType.RADIO
              ) {
                if (element.checked) {
                  formValues[name] = element.value;
                }
              } else if (
                element instanceof HTMLInputElement &&
                element.type === ElementType.CHECKBOX
              ) {
                formValues[name] = element.checked;
              } else if (
                element instanceof HTMLSelectElement &&
                element.multiple
              ) {
                const selectedOptions = Array.from(element.selectedOptions).map(
                  (option) => option.value
                );
                formValues[name] = selectedOptions;
              } else if (element instanceof HTMLSelectElement) {
                formValues[name] = [element.value]; // Tekli seçim bile array olarak döner
              } else {
                formValues[name] = element.value;
              }
            }
          }
        });

        // CustomForm elemanlarını kontrol etme
        elements.forEach((element) => {
          if ("value" in element && element.type === ElementType.RICHTEXTBOX) {
            formValues[element.id!] = element.value; // RichTextbox değerini ekleme
          }
        });

        await onSubmit(formValues);
      } catch (error) {
        toast.error("Form gönderimi sırasında bir hata oluştu.");
      } finally {
        setIsLoading(false); // Loading state'i durdur
      }
    } else {
      toast.error("Gerekli tüm alanları doldurunuz.");
      form.classList.add("was-validated");
    }
  };

  // Elemanları satır, sütun ve iç satıra göre gruplama
  const groupedElements = elements.reduce<
    Record<number, Record<number, Record<number, FormElement[]>>>
  >((acc, el) => {
    const row = el.rowId || 0;
    const col = el.colId || 0;
    const innerRow = el.innerRowId || 0;

    if (!acc[row]) acc[row] = {};
    if (!acc[row][col]) acc[row][col] = {};
    if (!acc[row][col][innerRow]) acc[row][col][innerRow] = [];

    acc[row][col][innerRow].push(el);
    return acc;
  }, {});

  return (
    <form
      ref={formRef}
      noValidate
      className="needs-validation"
      autoComplete="off"
    >
      {Object.entries(groupedElements).map(([rowId, columns]) => (
        <div className="row" key={`row-${rowId}`}>
          {Object.entries(columns).map(([colId, innerRows]) => (
            <div className="col" key={`col-${rowId}-${colId}`}>
              {Object.entries(innerRows).map(([innerRowId, elements]) => (
                <div
                  className={`row ${innerRowCustomClass}`}
                  key={`inner-row-${rowId}-${colId}-${innerRowId}`}
                >
                  {elements.map((element) => (
                    <div
                      className={element.colClass || "col-12"}
                      key={element.id}
                    >
                      {element.type === ElementType.TEXT ? (
                        <CustomText {...(element as TextElement)} />
                      ) : element.type === ElementType.INPUT ? (
                        <CustomInput {...(element as InputElement)} />
                      ) : element.type === ElementType.TEXTAREA ? (
                        <CustomTextarea {...(element as TextareaElement)} />
                      ) : element.type === ElementType.CHECKBOX ? (
                        <CustomCheckbox {...(element as CheckboxElement)} />
                      ) : element.type === ElementType.RADIO ? (
                        <CustomRadioButton
                          {...(element as RadioButtonElement)}
                        />
                      ) : element.type === ElementType.SELECT ? (
                        <CustomSelect {...(element as SelectElement)} />
                      ) : element.type === ElementType.DATETIMEPICKER ? (
                        <CustomDateTimePicker
                          {...(element as DateTimePickerElement)}
                        />
                      ) : element.type === ElementType.DIVIDER ? (
                        <CustomDivider {...(element as DividerElement)} />
                      ) : element.type === ElementType.RICHTEXTBOX ? (
                        <CustomRichTextbox
                          {...(element as RichTextboxElement)}
                        />
                      ) : element.type === ElementType.TREEVIEW ? (
                        <CustomTreeView {...(element as TreeViewElement)} />
                      ) : element.type === ElementType.BUTTON ? (
                        <CustomButton
                          {...(element as ButtonElement)}
                          isLoading={isLoading}
                        />
                      ) : element.type === ElementType.AUTOCOMPLETEINPUT ? (
                        <CustomAutocompleteInput
                          {...(element as AutoCompleteInputElement)}
                        />
                      ) : element.type === ElementType.FILEUPLOADER ? (
                        // FILEUPLOADER elemanı için CustomFileUploader'ı render ediyoruz
                        <>
                          {/* {element.label && <CustomText value={element.label} />} */}
                          <CustomFileUploader
                            url={(element as FileUploaderElement).url}
                            multi={(element as FileUploaderElement).multi}
                            allowedTypes={(element as FileUploaderElement).allowedTypes}
                            maxFile={(element as FileUploaderElement).maxFile}
                            maxSize={(element as FileUploaderElement).maxSize}
                            onUploadComplete={(element as FileUploaderElement).onUploadComplete}
                            onRemoveUploaded={(element as FileUploaderElement).onRemoveUploaded}
                            onPendingChange={(element as FileUploaderElement).onPendingChange}
                            label={(element as FileUploaderElement).label}
                          />
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
      {(showConfirmButton || showCancelButton) && (
        <div className="card-footer mt-3">
          <div
            className="form-actions mt-3"
            style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
          >
            {showCancelButton && (
              <CustomButton
                label={cancelLabel}
                className="btn btn-secondary"
                onClick={handleCancel || (() => { })}
                isLoading={isLoading}
              />
            )}
            {showConfirmButton && (
              <CustomButton
                label={confirmLabel}
                className="btn btn-primary"
                onClick={handleConfirm}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      )}
    </form>
  );
};

export { CustomForm, ElementType };
export default CustomForm;
